import React from 'react';
import { motion } from 'framer-motion';
import { RefreshCw, Calendar } from 'lucide-react';
import { BackupListProps } from './types';
import BackupTableView from './BackupTableView';
import BulkActionsBar from './BulkActionsBar';
import BackupMobileList from './BackupMobileList';
import { formatDate } from './utils';

const BackupList: React.FC<BackupListProps> = ({
  backups,
  loading,
  selectedBackups,
  onBackupSelect,
  onBulkAction,
  onRestoreBackup,
}) => {
  const handleDownload = async (backupId: string, zoneName: string) => {
    try {
      const response = await fetch(`/api/backups/${backupId}/download`);
      if (response.ok) {
        const blob = await response.blob();
        const url = window.URL.createObjectURL(blob);
        const a = document.createElement('a');
        a.href = url;
        a.download = `backup-${zoneName}-${backupId}.json`;
        a.click();
        window.URL.revokeObjectURL(url);
      }
    } catch (error) {
      console.error('Failed to download backup:', error);
    }
  };

  const handleDelete = async (backupId: string) => {
    if (window.confirm('Are you sure you want to delete this backup?')) {
      try {
        const response = await fetch(`/api/backups/${backupId}`, { method: 'DELETE' });
        if (response.ok) {
          // Trigger a refresh of the backup list
          window.location.reload();
        }
      } catch (error) {
        console.error('Failed to delete backup:', error);
      }
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <RefreshCw className="w-8 h-8 animate-spin text-blue-600" />
        <span className="ml-3 text-slate-600 dark:text-slate-400">Loading backups...</span>
      </div>
    );
  }

  if (backups.length === 0) {
    return (
      <div className="text-center py-12 bg-white dark:bg-slate-800 rounded-lg shadow-sm border border-slate-200 dark:border-slate-700">
        <div className="w-16 h-16 bg-slate-100 dark:bg-slate-700 rounded-full flex items-center justify-center mx-auto mb-4">
          <Calendar className="w-8 h-8 text-slate-400" />
        </div>
        <h3 className="text-lg font-medium text-slate-900 dark:text-slate-100 mb-2">
          No backups yet
        </h3>
        <p className="text-slate-500 dark:text-slate-400">
          Create your first backup to get started with DNS protection
        </p>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Bulk Actions */}
      {selectedBackups.length > 0 && (
        <motion.div initial={{ opacity: 0, y: -20 }} animate={{ opacity: 1, y: 0 }}>
          <BulkActionsBar
            selectedCount={selectedBackups.length}
            onDownloadAll={() => onBulkAction('download')}
            onDeleteSelected={() => onBulkAction('delete')}
          />
        </motion.div>
      )}

      {/* Desktop Table View */}
      <BackupTableView
        backups={backups}
        selectedBackups={selectedBackups}
        onBackupSelect={onBackupSelect}
        onRestoreBackup={onRestoreBackup}
      />

      {/* Mobile Card View */}
      <BackupMobileList
        backups={backups}
        selectedBackups={selectedBackups}
        onBackupSelect={onBackupSelect}
        onRestoreBackup={onRestoreBackup}
      />
    </div>
  );
};

export default BackupList;
